import { logger } from '../../utils/logger';
import React, { useEffect, useMemo, useState } from 'react';
import { PackageIcon, PlusIcon, SearchIcon, EditIcon, TrashIcon, ShoppingCartIcon, CalendarIcon, UserIcon, TagIcon, DollarSignIcon, ArchiveIcon, ArchiveRestoreIcon, AlertTriangleIcon, BarChart2Icon, TrendingUpIcon, TrendingDownIcon, BoxIcon, DownloadIcon, FileTextIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { FormSection } from '../ui/FormSection';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { Product, ProductSale, ProductSaleItem } from '../../types';
import { formatCurrency, getToday } from '../../utils/helpers';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import { supabase } from '../../utils/supabase';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
import { exportProductsToExcel, exportProductSalesToExcel, exportProductStatsToExcel } from '../../utils/excelExport';
import { exportProductsToPdf, exportProductSalesToPdf, exportProductStatsToPdf } from '../../utils/pdfExport';
import { checkLowStockAlerts, clearStockAlert } from '../../utils/stockAlertService';
// Helper to normalize names for comparison
function normalizeName(name: string) {
  if (!name) return '';
  return name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/^(dr\.|dra\.|lic\.|ing\.|ter\.)\s*/g, '').replace(/\s+/g, ' ');
}
const CATEGORIES = [{
  value: 'Cuidado Facial',
  label: 'Cuidado Facial'
}, {
  value: 'Cuidado Corporal',
  label: 'Cuidado Corporal'
}, {
  value: 'Suplementos',
  label: 'Suplementos'
}, {
  value: 'Equipos',
  label: 'Equipos'
}, {
  value: 'Accesorios',
  label: 'Accesorios'
}, {
  value: 'Otro',
  label: 'Otro'
}];

// ─── helpers: Supabase row → app type ────────────────────────────────────────
function rowToProduct(row: any): Product {
  return {
    id: row.id,
    name: row.name,
    sku: row.sku,
    description: row.description ?? '',
    price: Number(row.price),
    cost: Number(row.cost),
    stock: row.stock,
    minStock: row.min_stock ?? 5,
    category: row.category,
    status: row.is_archived ? 'archived' as any : row.status,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at)
  };
}
function rowToSale(row: any): ProductSale {
  return {
    id: row.id,
    date: row.date,
    patientId: row.patient_id ?? '',
    items: row.items as ProductSaleItem[],
    total: Number(row.total),
    notes: row.notes ?? '',
    createdByUserId: row.created_by_user_id,
    createdByUserName: row.created_by_user_name,
    createdAt: new Date(row.created_at)
  };
}
export function Products({
  autoOpenNew = false
}: {
  autoOpenNew?: boolean;
} = {}) {
  const {
    patients,
    addAuditLog
  } = useData();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    addNotification
  } = useNotifications();
  const {
    isAdmin,
    isTherapist,
    getPermissionLevel,
    canExport
  } = useRolePermissions();
  const canEditSales = isAdmin || isTherapist;
  const statsPermLevel = isAdmin ? 'full' : getPermissionLevel('products_stats') ?? 'none';
  const canViewProductStats = statsPermLevel === 'full' || statsPermLevel === 'read';
  const canExportExcel = canExport('products');
  const canExportPdf = canExport('products');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [stockBannerExpanded, setStockBannerExpanded] = useState(false);
  // Sales exports
  const [isSalesExcelModalOpen, setIsSalesExcelModalOpen] = useState(false);
  const [isSalesExcelExporting, setIsSalesExcelExporting] = useState(false);
  const [isSalesPdfModalOpen, setIsSalesPdfModalOpen] = useState(false);
  const [isSalesPdfExporting, setIsSalesPdfExporting] = useState(false);
  // Stats exports
  const [isStatsExcelModalOpen, setIsStatsExcelModalOpen] = useState(false);
  const [isStatsExcelExporting, setIsStatsExcelExporting] = useState(false);
  const [isStatsPdfModalOpen, setIsStatsPdfModalOpen] = useState(false);
  const [isStatsPdfExporting, setIsStatsPdfExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('catalog');
  const [loading, setLoading] = useState(true);
  // Data State
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<ProductSale[]>([]);
  const [sellers, setSellers] = useState<{
    id: string;
    name: string;
  }[]>([]);
  // ─── Load Data from Supabase ─────────────────────────────────────────────
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [{
          data: productsData,
          error: productsError
        }, {
          data: salesData,
          error: salesError
        }, {
          data: usersData
        }, {
          data: doctorsData
        }] = await Promise.all([supabase.from('products').select('*').order('created_at', {
          ascending: false
        }), supabase.from('product_sales').select('*').order('date', {
          ascending: false
        }), supabase.from('users').select('id, name, role'), supabase.from('doctors').select('id, name')]);
        if (productsError) throw productsError;
        if (salesError) throw salesError;
        const loadedProducts = (productsData ?? []).map(rowToProduct);
        setProducts(loadedProducts);
        setSales((salesData ?? []).map(rowToSale));
        // Stock alerts on initial load are handled by App.tsx (runs once per login)
        // Do NOT call checkLowStockAlerts here - it would fire every time the module mounts
        // Build sellers list: users with allowed roles + doctors, deduplicated by name
        const allowedRoles = new Set(['doctor', 'doctora', 'terapeuta', 'terapista', 'recepcion', 'recepción', 'recepcionista', 'callcenter', 'call center', 'call_center']);
        const sellersList: {
          id: string;
          name: string;
        }[] = [];
        const seenNormalizedNames = new Set<string>();
        (usersData ?? []).forEach((u: any) => {
          if (!allowedRoles.has((u.role || '').toLowerCase())) return;
          const norm = normalizeName(u.name);
          if (norm && !seenNormalizedNames.has(norm)) {
            seenNormalizedNames.add(norm);
            sellersList.push({
              id: u.id,
              name: u.name
            });
          }
        });
        (doctorsData ?? []).forEach((d: any) => {
          const norm = normalizeName(d.name);
          if (norm && !seenNormalizedNames.has(norm)) {
            seenNormalizedNames.add(norm);
            sellersList.push({
              id: d.id,
              name: d.name
            });
          }
        });
        setSellers(sellersList);
      } catch (e) {
        logger.error('Error loading data from Supabase:', e);
        addNotification({
          type: 'error',
          title: 'Error de carga',
          message: 'No se pudieron cargar los productos desde la base de datos.'
        });
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);
  // ─── CATALOG TAB STATE ───────────────────────────────────────────────────
  const [productSearch, setProductSearch] = useState('');
  const [lowStockFilter, setLowStockFilter] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    sku: '',
    description: '',
    price: '',
    cost: '',
    stock: '',
    minStock: '5',
    category: 'Cuidado Facial',
    status: 'active' as 'active' | 'inactive'
  });
  const [productErrors, setProductErrors] = useState<Record<string, string>>({});
  const [archiveConfirmProduct, setArchiveConfirmProduct] = useState<Product | null>(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const filteredProducts = products.filter(p => {
    if (p.status as any === 'archived') return false;
    if (lowStockFilter && p.stock > (p.minStock ?? 5)) return false;
    if (productSearch) {
      const q = productSearch.toLowerCase();
      return p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
    }
    return true;
  });
  const archivedProducts = products.filter(p => p.status as any === 'archived');
  /* ── Stats computations ─────────────────────────────────────────────── */
  const [statsDateFrom, setStatsDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 2);
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [statsDateTo, setStatsDateTo] = useState<string>(getToday());
  const [selectedStatProducts, setSelectedStatProducts] = useState<string[]>([]); // empty = all
  const [productSelectorSearch, setProductSelectorSearch] = useState('');
  const productStats = useMemo(() => {
    const filtered = sales.filter(s => (!statsDateFrom || s.date >= statsDateFrom) && (!statsDateTo || s.date <= statsDateTo));
    const map: Record<string, {
      id: string;
      name: string;
      category: string;
      revenue: number;
      units: number;
      salesCount: number;
      cost: number;
    }> = {};
    filtered.forEach(sale => {
      sale.items.forEach((item: any) => {
        // If products are selected, only include those
        if (selectedStatProducts.length > 0 && !selectedStatProducts.includes(item.productId)) return;
        const prod = products.find(p => p.id === item.productId);
        const id = item.productId;
        const name = prod?.name ?? item.productName ?? 'Desconocido';
        const cat = prod?.category ?? '—';
        const cost = (prod?.cost ?? 0) * Number(item.quantity ?? 0);
        if (!map[id]) map[id] = {
          id,
          name,
          category: cat,
          revenue: 0,
          units: 0,
          salesCount: 0,
          cost: 0
        };
        map[id].revenue += Number(item.subtotal ?? 0);
        map[id].units += Number(item.quantity ?? 0);
        map[id].salesCount += 1;
        map[id].cost += cost;
      });
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [sales, products, statsDateFrom, statsDateTo, selectedStatProducts]);
  const totalStatsRevenue = productStats.reduce((s, p) => s + p.revenue, 0);
  const totalStatsUnits = productStats.reduce((s, p) => s + p.units, 0);
  const totalStatsProfit = productStats.reduce((s, p) => s + (p.revenue - p.cost), 0);
  // Top 5 products by revenue for the chart
  const top5ProductStats = useMemo(() => productStats.slice(0, 5), [productStats]);
  // Revenue trend by month (last months in range)
  const revenueTrend = useMemo(() => {
    const months: Record<string, number> = {};
    sales.filter(s => (!statsDateFrom || s.date >= statsDateFrom) && (!statsDateTo || s.date <= statsDateTo)).forEach(sale => {
      const key = sale.date.slice(0, 7); // YYYY-MM
      months[key] = (months[key] || 0) + sale.total;
    });
    return Object.entries(months).sort(([a], [b]) => a.localeCompare(b)).map(([month, amount]) => ({
      month: new Date(month + '-01').toLocaleDateString('es', {
        month: 'short',
        year: '2-digit'
      }),
      amount
    }));
  }, [sales, statsDateFrom, statsDateTo]);
  const {
    sortedItems: sortedProducts,
    requestSort: requestProductSort,
    isSorted: isProductSorted
  } = useSortableData(filteredProducts);
  const {
    paginatedItems: paginatedProducts,
    currentPage: productPage,
    totalPages: productTotalPages,
    totalItems: productTotalItems,
    startIndex: productStartIndex,
    endIndex: productEndIndex,
    goToPage: productGoToPage,
    nextPage: productNextPage,
    prevPage: productPrevPage
  } = usePagination(sortedProducts);
  // Pagination for the stats detail table
  const {
    paginatedItems: paginatedProductStats,
    currentPage: statsTablePage,
    totalPages: statsTableTotalPages,
    totalItems: statsTableTotalItems,
    startIndex: statsTableStartIndex,
    endIndex: statsTableEndIndex,
    goToPage: statsTableGoToPage
  } = usePagination(productStats, {
    itemsPerPage: 10
  });
  const handleOpenProductModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        sku: product.sku,
        description: product.description,
        price: product.price.toString(),
        cost: product.cost.toString(),
        stock: product.stock.toString(),
        minStock: (product.minStock ?? 5).toString(),
        category: product.category,
        status: product.status
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        sku: '',
        description: '',
        price: '',
        cost: '',
        stock: '',
        minStock: '5',
        category: 'Cuidado Facial',
        status: 'active'
      });
    }
    setProductErrors({});
    setIsProductModalOpen(true);
  };
  // Auto-open new form when navigated with :new param
  React.useEffect(() => {
    if (autoOpenNew) {
      const t = setTimeout(() => handleOpenProductModal(), 100);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNew]);
  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!productForm.name.trim()) errors.name = 'El nombre es requerido';
    if (!productForm.sku.trim()) errors.sku = 'El SKU es requerido';
    if (!productForm.price || isNaN(Number(productForm.price))) errors.price = 'Precio inválido';
    if (!productForm.cost || isNaN(Number(productForm.cost))) errors.cost = 'Costo inválido';
    if (!productForm.stock || isNaN(Number(productForm.stock))) errors.stock = 'Stock inválido';
    if (Object.keys(errors).length > 0) {
      setProductErrors(errors);
      return;
    }
    const payload = {
      name: productForm.name,
      sku: productForm.sku,
      description: productForm.description,
      price: Number(productForm.price),
      cost: Number(productForm.cost),
      stock: Number(productForm.stock),
      min_stock: Number(productForm.minStock) || 5,
      category: productForm.category,
      status: productForm.status
    };
    try {
      if (editingProduct) {
        const {
          data,
          error
        } = await supabase.from('products').update(payload).eq('id', editingProduct.id).select().single();
        if (error) throw error;
        const updatedProduct = rowToProduct(data);
        const updatedList = products.map(p => p.id === editingProduct.id ? updatedProduct : p);
        setProducts(updatedList);
        // If stock was replenished above minStock, clear the alert record
        if (updatedProduct.stock > updatedProduct.minStock) {
          clearStockAlert(updatedProduct.id);
        } else {
          checkLowStockAlerts([updatedProduct], addNotification);
        }
        addNotification({
          type: 'success',
          title: 'Producto Actualizado',
          message: `${productForm.name} ha sido actualizado.`
        });
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'update',
          module: 'products',
          entityId: editingProduct.id,
          entityName: productForm.name,
          details: `Producto actualizado: ${productForm.name} (SKU: ${productForm.sku}) - Precio: ${formatCurrency(Number(productForm.price))}, Stock: ${productForm.stock}`
        });
      } else {
        const {
          data,
          error
        } = await supabase.from('products').insert(payload).select().single();
        if (error) throw error;
        const newProduct = rowToProduct(data);
        setProducts([newProduct, ...products]);
        checkLowStockAlerts([newProduct], addNotification);
        addNotification({
          type: 'success',
          title: 'Producto Creado',
          message: `${productForm.name} ha sido agregado al catálogo.`
        });
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'create',
          module: 'products',
          entityId: data.id,
          entityName: productForm.name,
          details: `Producto creado: ${productForm.name} (SKU: ${productForm.sku}) - Precio: ${formatCurrency(Number(productForm.price))}, Stock: ${productForm.stock}`
        });
      }
      setIsProductModalOpen(false);
    } catch (e: any) {
      logger.error('Error saving product:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo guardar el producto.'
      });
    }
  };
  const handleArchiveProduct = (id: string) => {
    const product = products.find(p => p.id === id);
    if (product) setArchiveConfirmProduct(product);
  };
  const confirmArchiveProduct = async () => {
    if (!archiveConfirmProduct) return;
    setArchiveLoading(true);
    try {
      const {
        error
      } = await supabase.from('products').update({
        is_archived: true
      }).eq('id', archiveConfirmProduct.id);
      if (error) throw error;
      setProducts(products.map(p => p.id === archiveConfirmProduct.id ? {
        ...p,
        status: 'archived' as any
      } : p));
      addNotification({
        type: 'success',
        title: 'Producto Archivado',
        message: `"${archiveConfirmProduct.name}" fue archivado. Puedes restaurarlo desde Productos Archivados.`
      });
      addAuditLog({
        userId: user?.id || 'system',
        userName: user?.name || 'Sistema',
        action: 'update',
        module: 'products',
        entityId: archiveConfirmProduct.id,
        entityName: archiveConfirmProduct.name,
        details: `Producto archivado: ${archiveConfirmProduct.name} (SKU: ${archiveConfirmProduct.sku || 'N/A'})`
      });
      setArchiveConfirmProduct(null);
    } catch (e: any) {
      logger.error('Error archiving product:', e);
      addNotification({
        type: 'error',
        title: 'Error al archivar',
        message: e.message ?? 'No se pudo archivar el producto.'
      });
    } finally {
      setArchiveLoading(false);
    }
  };
  const handleUnarchiveProduct = async (id: string) => {
    try {
      const product = products.find(p => p.id === id);
      const {
        error
      } = await supabase.from('products').update({
        is_archived: false
      }).eq('id', id);
      if (error) throw error;
      setProducts(products.map(p => p.id === id ? {
        ...p,
        status: 'active' as any
      } : p));
      addNotification({
        type: 'success',
        title: 'Producto Restaurado',
        message: `"${product?.name}" ha sido restaurado al catálogo.`
      });
      addAuditLog({
        userId: user?.id || 'system',
        userName: user?.name || 'Sistema',
        action: 'update',
        module: 'products',
        entityId: id,
        entityName: product?.name || 'Producto',
        details: `Producto restaurado del archivo: ${product?.name || 'Producto'} (SKU: ${product?.sku || 'N/A'})`
      });
    } catch (e: any) {
      logger.error('Error unarchiving product:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo restaurar el producto.'
      });
    }
  };
  // ─── SALES TAB STATE ─────────────────────────────────────────────────────
  const [saleSearch, setSaleSearch] = useState('');
  const [saleDateFrom, setSaleDateFrom] = useState('');
  const [saleDateTo, setSaleDateTo] = useState('');
  const [isSaleModalOpen, setIsSaleModalOpen] = useState(false);
  const [editingSale, setEditingSale] = useState<ProductSale | null>(null);
  const [saleForm, setSaleForm] = useState({
    patientId: '',
    sellerId: '',
    date: getToday(),
    notes: ''
  });
  const [saleItems, setSaleItems] = useState<{
    productId: string;
    quantity: number;
    discount: number;
  }[]>([]);
  const [saleErrors, setSaleErrors] = useState<Record<string, string>>({});
  const filteredSales = sales.filter(s => {
    const patient = patients.find(p => p.id === s.patientId);
    const matchesSearch = !saleSearch || patient?.name.toLowerCase().includes(saleSearch.toLowerCase()) || s.createdByUserName.toLowerCase().includes(saleSearch.toLowerCase());
    const matchesDateFrom = !saleDateFrom || s.date >= saleDateFrom;
    const matchesDateTo = !saleDateTo || s.date <= saleDateTo;
    return matchesSearch && matchesDateFrom && matchesDateTo;
  });
  const {
    sortedItems: sortedSales,
    requestSort: requestSaleSort,
    isSorted: isSaleSorted
  } = useSortableData(filteredSales);
  const {
    paginatedItems: paginatedSales,
    currentPage: salePage,
    totalPages: saleTotalPages,
    totalItems: saleTotalItems,
    startIndex: saleStartIndex,
    endIndex: saleEndIndex,
    goToPage: saleGoToPage,
    nextPage: saleNextPage,
    prevPage: salePrevPage
  } = usePagination(sortedSales);
  const handleOpenSaleModal = () => {
    setEditingSale(null);
    setSaleForm({
      patientId: '',
      sellerId: '',
      date: getToday(),
      notes: ''
    });
    setSaleItems([{
      productId: '',
      quantity: 1,
      discount: 0
    }]);
    setSaleErrors({});
    setIsSaleModalOpen(true);
  };
  const handleOpenEditSaleModal = (sale: ProductSale) => {
    setEditingSale(sale);
    setSaleForm({
      patientId: sale.patientId || '',
      sellerId: sale.createdByUserId || '',
      date: sale.date,
      notes: sale.notes || ''
    });
    setSaleItems(sale.items.map((item: any) => ({
      productId: item.productId,
      quantity: item.quantity,
      discount: item.discount || 0
    })));
    setSaleErrors({});
    setIsSaleModalOpen(true);
  };
  const addSaleItem = () => setSaleItems([...saleItems, {
    productId: '',
    quantity: 1,
    discount: 0
  }]);
  const removeSaleItem = (index: number) => setSaleItems(saleItems.filter((_, i) => i !== index));
  const updateSaleItem = (index: number, field: 'productId' | 'quantity' | 'discount', value: any) => {
    const newItems = [...saleItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    setSaleItems(newItems);
    // Clear any row-specific or global "items" error so stale messages don't linger
    setSaleErrors(prev => {
      if (!prev[`item_${index}`] && !prev.items) return prev;
      const next = {
        ...prev
      };
      delete next[`item_${index}`];
      delete next.items;
      return next;
    });
  };
  const calculateSaleTotal = () => saleItems.reduce((total, item) => {
    if (!item.productId) return total;
    const product = products.find(p => p.id === item.productId);
    if (!product) return total;
    const lineTotal = product.price * (item.quantity || 0);
    const discountAmount = lineTotal * ((item.discount || 0) / 100);
    return total + lineTotal - discountAmount;
  }, 0);
  const handleSaveSale = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!saleForm.patientId) errors.patientId = 'Debe seleccionar un cliente/paciente';
    if (!saleForm.sellerId) errors.sellerId = 'Debe seleccionar un vendedor';
    if (!saleForm.date) errors.date = 'La fecha es requerida';
    const validItems = saleItems.filter(item => item.productId && item.quantity > 0);
    if (validItems.length === 0) errors.items = 'Debe agregar al menos un producto válido';
    // For new sales only: check stock. For edits, we check net difference.
    if (!editingSale) {
      validItems.forEach((item, idx) => {
        const product = products.find(p => p.id === item.productId);
        if (product && product.stock < item.quantity) errors[`item_${idx}`] = `Stock insuficiente (${product.stock} disponibles)`;
      });
    } else {
      // For edits: check stock considering items returned from old sale
      validItems.forEach((item, idx) => {
        const product = products.find(p => p.id === item.productId);
        if (!product) return;
        const oldItem = editingSale.items.find((oi: any) => oi.productId === item.productId);
        const returnedQty = oldItem ? (oldItem as any).quantity : 0;
        const availableStock = product.stock + returnedQty;
        if (availableStock < item.quantity) errors[`item_${idx}`] = `Stock insuficiente (${availableStock} disponibles tras devolver la venta anterior)`;
      });
    }
    if (Object.keys(errors).length > 0) {
      setSaleErrors(errors);
      return;
    }
    const finalItems: ProductSaleItem[] = validItems.map(item => {
      const product = products.find(p => p.id === item.productId)!;
      const lineTotal = product.price * item.quantity;
      const discountAmount = lineTotal * ((item.discount || 0) / 100);
      return {
        productId: item.productId,
        productName: product.name,
        quantity: item.quantity,
        unitPrice: product.price,
        discount: item.discount || 0,
        subtotal: lineTotal - discountAmount
      };
    });
    const total = finalItems.reduce((sum, item) => sum + item.subtotal, 0);
    const selectedSeller = sellers.find(s => s.id === saleForm.sellerId);
    const sellerUserId = selectedSeller?.id ?? user?.id ?? 'unknown';
    const sellerUserName = selectedSeller?.name ?? user?.name ?? 'Usuario';
    try {
      if (editingSale) {
        // ── EDIT FLOW ──────────────────────────────────────────────────────
        const {
          error: updateError
        } = await supabase.from('product_sales').update({
          date: saleForm.date,
          patient_id: saleForm.patientId || null,
          items: finalItems,
          total,
          notes: saleForm.notes,
          created_by_user_id: sellerUserId,
          created_by_user_name: sellerUserName
        }).eq('id', editingSale.id);
        if (updateError) throw updateError;
        // Restore stock for old items, then deduct for new items
        const stockUpdates: Record<string, number> = {}
        // +restore old quantities
;
        (editingSale.items as any[]).forEach(oldItem => {
          stockUpdates[oldItem.productId] = (stockUpdates[oldItem.productId] || 0) + oldItem.quantity;
        });
        // -deduct new quantities
        finalItems.forEach(newItem => {
          stockUpdates[newItem.productId] = (stockUpdates[newItem.productId] || 0) - newItem.quantity;
        });
        await Promise.all(Object.entries(stockUpdates).map(([productId, delta]) => {
          const product = products.find(p => p.id === productId);
          if (!product) return Promise.resolve();
          return supabase.from('products').update({
            stock: product.stock + delta
          }).eq('id', productId);
        }));
        // Update local state
        setSales(sales.map(s => s.id === editingSale.id ? {
          ...s,
          date: saleForm.date,
          patientId: saleForm.patientId,
          items: finalItems,
          total,
          notes: saleForm.notes,
          createdByUserId: sellerUserId,
          createdByUserName: sellerUserName
        } : s));
        setProducts(products.map(p => {
          const delta = stockUpdates[p.id];
          return delta !== undefined ? {
            ...p,
            stock: p.stock + delta
          } : p;
        }));
        addNotification({
          type: 'success',
          title: 'Venta Actualizada',
          message: `Venta por ${formatCurrency(total)} actualizada exitosamente.`
        });
        const patientName = patients.find(p => p.id === saleForm.patientId)?.name || 'Cliente';
        const itemsSummary = finalItems.map(item => {
          const base = `${item.quantity}x ${item.productName}`;
          return item.discount ? `${base} (-${item.discount}% desc.)` : base;
        }).join(', ');
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'update',
          module: 'product_sales',
          entityId: editingSale.id,
          entityName: `Venta a ${patientName}`,
          details: `Venta EDITADA por ${user?.name || 'Usuario'}: ${itemsSummary} - Total: ${formatCurrency(total)} - Cliente: ${patientName} - Vendedor: ${sellerUserName}`
        });
      } else {
        // ── CREATE FLOW ────────────────────────────────────────────────────
        const {
          data: saleData,
          error: saleError
        } = await supabase.from('product_sales').insert({
          date: saleForm.date,
          patient_id: saleForm.patientId || null,
          items: finalItems,
          total,
          notes: saleForm.notes,
          created_by_user_id: sellerUserId,
          created_by_user_name: sellerUserName
        }).select().single();
        if (saleError) throw saleError;
        // Update stock for each product sold
        await Promise.all(finalItems.map(item => {
          const product = products.find(p => p.id === item.productId)!;
          return supabase.from('products').update({
            stock: product.stock - item.quantity
          }).eq('id', item.productId);
        }));
        setSales([rowToSale(saleData), ...sales]);
        const updatedProducts = products.map(p => {
          const soldItem = finalItems.find(item => item.productId === p.id);
          return soldItem ? {
            ...p,
            stock: p.stock - soldItem.quantity
          } : p;
        });
        setProducts(updatedProducts);

        // Check low stock for affected products
        const affectedProducts = updatedProducts.filter(p => finalItems.some(item => item.productId === p.id));
        if (affectedProducts.length > 0) {
          checkLowStockAlerts(affectedProducts, addNotification);
        }
        addNotification({
          type: 'success',
          title: 'Venta Registrada',
          message: `Venta por ${formatCurrency(total)} registrada exitosamente.`
        });
        const patientName = patients.find(p => p.id === saleForm.patientId)?.name || 'Cliente';
        const itemsSummary = finalItems.map(item => {
          const base = `${item.quantity}x ${item.productName}`;
          return item.discount ? `${base} (-${item.discount}% desc.)` : base;
        }).join(', ');
        const hasAnyDiscount = finalItems.some(item => (item.discount || 0) > 0);
        const discountNote = hasAnyDiscount ? ` | Descuento aplicado: ${finalItems.filter(item => (item.discount || 0) > 0).map(item => `${item.productName}: ${item.discount}% (${formatCurrency(item.unitPrice * item.quantity - item.subtotal)})`).join(', ')}` : '';
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'create',
          module: 'product_sales',
          entityId: saleData.id,
          entityName: `Venta a ${patientName}`,
          details: `Venta registrada por ${sellerUserName}: ${itemsSummary} - Total: ${formatCurrency(total)} - Cliente: ${patientName}${discountNote}`
        });
      }
      setIsSaleModalOpen(false);
      setEditingSale(null);
    } catch (e: any) {
      logger.error('Error saving sale:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo guardar la venta.'
      });
    }
  };
  const tabs = [{
    id: 'catalog',
    label: 'Catálogo de Productos',
    mobileLabel: 'Catálogo',
    icon: <PackageIcon className="w-4 h-4" data-id="element-4151" />
  }, {
    id: 'sales',
    label: 'Ventas de Productos',
    mobileLabel: 'Ventas',
    icon: <ShoppingCartIcon className="w-4 h-4" data-id="element-4152" />
  }, ...(canViewProductStats ? [{
    id: 'stats',
    label: statsPermLevel === 'read' ? 'Estadísticas (solo lectura)' : 'Estadísticas',
    mobileLabel: 'Estadísticas',
    icon: <BarChart2Icon className="w-4 h-4" data-id="element-4153" />
  }] : []), {
    id: 'archived',
    label: 'Productos Archivados',
    mobileLabel: 'Archivados',
    icon: <ArchiveIcon className="w-4 h-4" data-id="element-4154" />
  }];
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    exportProductsToExcel(products, dateFrom, dateTo);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    exportProductsToPdf(products);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const getPatientName = (id: string) => patients.find(p => p.id === id)?.name || 'Cliente';
  const handleSalesExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsSalesExcelExporting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    exportProductSalesToExcel(filteredSales, getPatientName, dateFrom, dateTo);
    setIsSalesExcelExporting(false);
    setIsSalesExcelModalOpen(false);
  };
  const handleSalesExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsSalesPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    await exportProductSalesToPdf(filteredSales, getPatientName, dateFrom, dateTo);
    setIsSalesPdfExporting(false);
    setIsSalesPdfModalOpen(false);
  };
  const handleStatsExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsStatsExcelExporting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    exportProductStatsToExcel(productStats, revenueTrend, {
      revenue: totalStatsRevenue,
      units: totalStatsUnits,
      profit: totalStatsProfit
    }, statsDateFrom, statsDateTo);
    setIsStatsExcelExporting(false);
    setIsStatsExcelModalOpen(false);
  };
  const handleStatsExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsStatsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    await exportProductStatsToPdf(productStats, revenueTrend, {
      revenue: totalStatsRevenue,
      units: totalStatsUnits,
      profit: totalStatsProfit
    }, statsDateFrom, statsDateTo);
    setIsStatsPdfExporting(false);
    setIsStatsPdfModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-4155">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-600 via-orange-500 to-rose-500 shadow-xl text-white" data-id="element-4156">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px)'
      }} data-id="element-4157" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-4158" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-4159" />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full border-[40px] border-white/10 pointer-events-none" data-id="element-4160" />
        <div className="absolute -bottom-10 right-32 w-40 h-40 rounded-full opacity-10 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)'
      }} data-id="element-4161" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-4162" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-4163">
          <div data-id="element-4164">
            <div className="flex items-center gap-3 mb-2" data-id="element-4165">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-4166">
                <PackageIcon className="w-6 h-6 text-white" data-id="element-4167" />
              </div>
              <h1 className="text-2xl font-bold text-white" data-id="element-4168">Productos</h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-4169">
              Gestión de inventario y ventas de productos
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-4170">
            {/* Catalog exports */}
            {activeTab === 'catalog' && canExportPdf && <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-4172" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4171">
                Exportar PDF
              </Button>}
            {activeTab === 'catalog' && canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-4174" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4173">
                Exportar Excel
              </Button>}

            {/* Sales exports */}
            {activeTab === 'sales' && <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-4176" />} onClick={() => setIsSalesPdfModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4175">
                Exportar PDF
              </Button>}
            {activeTab === 'sales' && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-4178" />} onClick={() => setIsSalesExcelModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4177">
                Exportar Excel
              </Button>}

            {/* Stats exports */}
            {activeTab === 'stats' && <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-4180" />} onClick={() => setIsStatsPdfModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4179">
                Exportar PDF
              </Button>}
            {activeTab === 'stats' && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-4182" />} onClick={() => setIsStatsExcelModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4181">
                Exportar Excel
              </Button>}

            {/* Action button */}
            {activeTab === 'catalog' ? <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-4184" />} onClick={() => handleOpenProductModal()} className="bg-white text-orange-700 hover:bg-orange-50 shadow-lg shadow-orange-900/20" data-id="element-4183">
                Nuevo Producto
              </Button> : <Button leftIcon={<ShoppingCartIcon className="w-4 h-4" data-id="element-4186" />} onClick={handleOpenSaleModal} className="bg-white text-orange-700 hover:bg-orange-50 shadow-lg shadow-orange-900/20" data-id="element-4185">
                Registrar Venta
              </Button>}
          </div>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-4187" />

      {/* CATALOG TAB */}
      {activeTab === 'catalog' && <Card padding="none" data-id="element-4188">

          {/* ── LOW STOCK BANNER ─────────────────────────────────────────── */}
          {(() => {
        const lowStockItems = products.filter(p => p.stock <= (p.minStock ?? 5) && p.status as any !== 'archived');
        if (lowStockItems.length === 0) return null;
        const outOfStock = lowStockItems.filter(p => p.stock === 0);
        const lowOnly = lowStockItems.filter(p => p.stock > 0);
        const previewItems = lowStockItems.slice(0, 8);
        const extraCount = lowStockItems.length - 8;
        return <div className="mx-3 sm:mx-4 mt-3 sm:mt-4 rounded-2xl overflow-hidden" style={{
          background: 'linear-gradient(135deg, #fff7ed 0%, #fff 60%)',
          border: '1px solid #fed7aa'
        }} data-id="element-4189">

                {/* Header */}
                <button onClick={() => {
            if (lowStockFilter) {
              setLowStockFilter(false);
            } else {
              setLowStockFilter(true);
              setStockBannerExpanded(false);
              setTimeout(() => {
                document.getElementById('catalog-search')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest'
                });
              }, 100);
            }
          }} className="w-full px-4 py-3 text-left transition-colors hover:bg-orange-50/50" style={{
            borderBottom: '1px solid #ffedd5',
            background: 'none',
            cursor: 'pointer'
          }} data-id="element-4190">

                  {/* Top row: icon + title + arrow */}
                  <div className="flex items-center gap-3" data-id="element-4191">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{
                background: 'linear-gradient(135deg, #f97316, #ef4444)'
              }} data-id="element-4192">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" data-id="element-4193">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" data-id="element-4194" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" data-id="element-4195" />
                      </svg>
                    </div>
                    <p className="text-sm font-bold text-gray-800 flex-1" data-id="element-4196">Alerta de inventario</p>
                    <svg className="w-4 h-4 text-orange-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" data-id="element-4197">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" data-id="element-4198" />
                    </svg>
                  </div>

                  {/* Bottom row: subtitle + badges */}
                  <div className="flex items-center justify-between gap-2 mt-1.5 pl-11" data-id="element-4199">
                    <p className="text-xs text-gray-500 flex-1 min-w-0 truncate" data-id="element-4200">
                      {lowStockFilter ? '✓ Mostrando solo stock bajo — toca para quitar' : 'Toca para filtrar productos críticos'}
                    </p>
                    <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-4201">
                      {outOfStock.length > 0 && <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold text-white" style={{
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)'
                }} data-id="element-4202">
                          <span className="w-1.5 h-1.5 rounded-full bg-white/70 animate-pulse" data-id="element-4203" />
                          {outOfStock.length} agotados
                        </div>}
                      {lowOnly.length > 0 && <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold" style={{
                  background: '#fff7ed',
                  color: '#c2410c',
                  border: '1px solid #fed7aa'
                }} data-id="element-4204">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400" data-id="element-4205" />
                          {lowOnly.length} bajo
                        </div>}
                    </div>
                  </div>
                </button>

                {/* Preview chips row */}
                <div className="px-3 sm:px-4 pt-3 pb-1 flex gap-2 overflow-x-auto" style={{
            scrollbarWidth: 'none'
          }} data-id="element-4206">
                  {previewItems.map(p => <div key={p.id} className="flex items-center gap-2 rounded-xl flex-shrink-0 text-xs font-medium" style={{
              padding: '6px 12px 6px 8px',
              background: p.stock === 0 ? '#fef2f2' : '#fff7ed',
              border: `1px solid ${p.stock === 0 ? '#fecaca' : '#fed7aa'}`,
              color: p.stock === 0 ? '#dc2626' : '#c2410c'
            }} data-id="element-4207">
                      <span className="w-5 h-5 rounded-lg flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0" style={{
                background: p.stock === 0 ? '#ef4444' : '#f97316',
                minWidth: '20px'
              }} data-id="element-4208">
                        {p.stock}
                      </span>
                      <span className="truncate" style={{
                maxWidth: '140px'
              }} data-id="element-4209">{p.name}</span>
                    </div>)}
                <div className="flex-shrink-0 w-2" data-id="element-4210" />
                </div>
              </div>;
      })()}

          <div id="catalog-search" className="p-4 border-b border-gray-200 dark:border-gray-700" data-id="element-4211">
            <div className="flex items-center gap-3 flex-wrap" data-id="element-4212">
              <div className="relative flex-1 min-w-[200px] max-w-md" data-id="element-4213">
                <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-4214" />
                <Input style={{
              paddingLeft: "2.5rem"
            }} placeholder="Buscar por nombre, SKU o categoría..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="" data-id="element-4215" />
              </div>
              {lowStockFilter && <button onClick={() => setLowStockFilter(false)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80" style={{
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            color: '#c2410c',
            cursor: 'pointer'
          }} data-id="element-4216">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400" data-id="element-4217" />
                  Stock bajo activo
                  <svg className="w-3 h-3 ml-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" data-id="element-4218">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" data-id="element-4219" />
                  </svg>
                </button>}
            </div>
          </div>

          {/* ── MOBILE CARDS ── */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4220">
            {paginatedProducts.length === 0 ? <div className="py-8 text-center text-gray-400 text-sm" data-id="element-4221">
                No se encontraron productos
              </div> : paginatedProducts.map(product => <div key={product.id} className="px-4 py-3 flex items-center gap-3" data-id="element-4222">
            
                  <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400 flex-shrink-0" data-id="element-4223">
                    <PackageIcon className="w-4 h-4" data-id="element-4224" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4225">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-4226">
                      {product.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-4227">
                      {product.category} · SKU: {product.sku}
                    </p>
                    <div className="flex items-center gap-2 mt-1" data-id="element-4228">
                      <span className="text-xs font-semibold text-gray-900 dark:text-white" data-id="element-4229">
                        {formatCurrency(product.price)}
                      </span>
                      <span className={`text-xs font-medium ${product.stock <= 5 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`} data-id="element-4230">
                  
                        Stock: {product.stock}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-4231">
                    <button onClick={() => handleOpenProductModal(product)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" data-id="element-4232">
                
                      <EditIcon className="w-4 h-4" data-id="element-4233" />
                    </button>
                    <button onClick={() => handleArchiveProduct(product.id)} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar producto" data-id="element-4234">
                
                      <ArchiveIcon className="w-4 h-4" data-id="element-4235" />
                    </button>
                  </div>
                </div>)}
          </div>

          {/* ── DESKTOP TABLE ── */}
          <div className="hidden sm:block overflow-x-auto" data-id="element-4236">
            <table className="w-full" data-id="element-4237">
              <thead data-id="element-4238">
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-4239">
                  <SortableHeader label="Producto" sortKey="name" currentDirection={isProductSorted('name')} onSort={requestProductSort} data-id="element-4240" />
                

                  <SortableHeader label="SKU" sortKey="sku" currentDirection={isProductSorted('sku')} onSort={requestProductSort} className="hidden sm:table-cell" data-id="element-4241" />
                

                  <SortableHeader label="Categoría" sortKey="category" currentDirection={isProductSorted('category')} onSort={requestProductSort} className="hidden sm:table-cell" data-id="element-4242" />
                

                  <SortableHeader label="Precio" sortKey="price" currentDirection={isProductSorted('price')} onSort={requestProductSort} data-id="element-4243" />
                

                  <SortableHeader label="Stock" sortKey="stock" currentDirection={isProductSorted('stock')} onSort={requestProductSort} data-id="element-4244" />
                

                  <SortableHeader label="Estado" sortKey="status" currentDirection={isProductSorted('status')} onSort={requestProductSort} data-id="element-4245" />
                

                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4246">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4247">
                {loading ? <tr data-id="element-4248">
                    <td colSpan={7} className="py-8 text-center text-gray-400" data-id="element-4249">
                      Cargando productos...
                    </td>
                  </tr> : paginatedProducts.length === 0 ? <tr data-id="element-4250">
                    <td colSpan={7} className="py-8 text-center text-gray-500" data-id="element-4251">
                      No se encontraron productos
                    </td>
                  </tr> : paginatedProducts.map(product => <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-4252">
                
                      <td className="py-3 px-4" data-id="element-4253">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-4254">
                          {product.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate max-w-[200px]" data-id="element-4255">
                          {product.description}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300 hidden sm:table-cell" data-id="element-4256">
                        {product.sku}
                      </td>
                      <td className="py-3 px-4 hidden sm:table-cell" data-id="element-4257">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-4258">
                          <TagIcon className="w-3 h-3" data-id="element-4259" />
                          {product.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white" data-id="element-4260">
                        {formatCurrency(product.price)}
                      </td>
                      <td className="py-3 px-4" data-id="element-4261">
                        {product.stock <= (product.minStock ?? 5) ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${product.stock === 0 ? 'bg-red-600 text-white' : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700'}`} data-id="element-4262">
                            {product.stock}
                          </span> : <span className="font-medium text-gray-900 dark:text-white" data-id="element-4263">
                            {product.stock}
                          </span>}
                      </td>
                      <td className="py-3 px-4" data-id="element-4264">
                        <Badge variant={product.status === 'active' ? 'success' : 'default'} data-id="element-4265">
                    
                          {product.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-4266">
                        <div className="flex justify-end gap-2" data-id="element-4267">
                          <button onClick={() => handleOpenProductModal(product)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar producto" data-id="element-4268">
                      
                            <EditIcon className="w-4 h-4" data-id="element-4269" />
                          </button>
                          <button onClick={() => handleArchiveProduct(product.id)} className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Archivar producto" data-id="element-4270">
                      
                            <ArchiveIcon className="w-4 h-4" data-id="element-4271" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-4272">
            <Pagination currentPage={productPage} totalPages={productTotalPages} totalItems={productTotalItems} startIndex={productStartIndex} endIndex={productEndIndex} onPageChange={productGoToPage} onNext={productNextPage} onPrev={productPrevPage} data-id="element-4273" />
          
          </div>
        </Card>}

      {/* SALES TAB */}
      {activeTab === 'sales' && <Card padding="none" data-id="element-4274">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-3" data-id="element-4275">
            <div className="relative" data-id="element-4276">
              <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-4277" />
              <Input style={{
            paddingLeft: "2.5rem"
          }} placeholder="Buscar por cliente o vendedor..." value={saleSearch} onChange={e => setSaleSearch(e.target.value)} className="" data-id="element-4278" />
            </div>
            <div className="flex items-center gap-2" data-id="element-4279">
              <input type="date" value={saleDateFrom} onChange={e => setSaleDateFrom(e.target.value)} className="flex-1 min-w-0 w-0 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400" data-id="element-4280" />
              <span className="text-gray-400 flex-shrink-0 font-medium" data-id="element-4281">→</span>
              <input type="date" value={saleDateTo} onChange={e => setSaleDateTo(e.target.value)} className="flex-1 min-w-0 w-0 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400" data-id="element-4282" />
            </div>
          </div>

          {/* ── MOBILE CARDS VENTAS ── */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4283">
            {paginatedSales.length === 0 ? <div className="py-8 text-center text-gray-400 text-sm" data-id="element-4284">
                No se encontraron ventas
              </div> : paginatedSales.map(sale => {
          const patient = patients.find(p => p.id === sale.patientId);
          const saleItems = sale.items || [];
          return <div key={sale.id} className="px-4 py-3 flex items-start gap-3" data-id="element-4285">
                
                    <div className="flex-1 min-w-0" data-id="element-4286">
                      <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-4287">
                        {patient?.name || sale.patientName || 'Cliente'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4288">
                        {sale.date} · {sale.createdByUserName || ''}
                      </p>
                      <p className="text-xs text-gray-400 truncate" data-id="element-4289">
                        {saleItems.map((i: any) => {
                  const name = i.productName || '';
                  const disc = i.discount ? ` (-${i.discount}%)` : '';
                  return `${i.quantity || 1}x ${name}${disc}`;
                }).join(', ')}
                      </p>
                      <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 mt-1" data-id="element-4290">
                        {formatCurrency(sale.total)}
                      </p>
                    </div>
                    {canEditSales && <button onClick={() => handleOpenEditSaleModal(sale)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors flex-shrink-0" title="Editar venta" data-id="element-4291">
                  
                        <EditIcon className="w-4 h-4" data-id="element-4292" />
                      </button>}
                  </div>;
        })}
          </div>

          {/* ── DESKTOP TABLE VENTAS ── */}
          <div className="hidden sm:block overflow-x-auto" data-id="element-4293">
            <table className="w-full" data-id="element-4294">
              <thead data-id="element-4295">
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-4296">
                  <SortableHeader label="Fecha" sortKey="date" currentDirection={isSaleSorted('date')} onSort={requestSaleSort} data-id="element-4297" />
                

                  <SortableHeader label="Cliente/Paciente" sortKey="patientId" currentDirection={isSaleSorted('patientId')} onSort={requestSaleSort} data-id="element-4298" />
                

                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4299">
                    Productos
                  </th>
                  <SortableHeader label="Total" sortKey="total" currentDirection={isSaleSorted('total')} onSort={requestSaleSort} data-id="element-4300" />
                

                  <SortableHeader label="Vendedor" sortKey="createdByUserName" currentDirection={isSaleSorted('createdByUserName')} onSort={requestSaleSort} data-id="element-4301" />
                

                  {canEditSales && <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4302">
                      Acciones
                    </th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4303">
                {loading ? <tr data-id="element-4304">
                    <td colSpan={canEditSales ? 6 : 5} className="py-8 text-center text-gray-400" data-id="element-4305">
                  
                      Cargando ventas...
                    </td>
                  </tr> : paginatedSales.length === 0 ? <tr data-id="element-4306">
                    <td colSpan={canEditSales ? 6 : 5} className="py-8 text-center text-gray-500" data-id="element-4307">
                  
                      No se encontraron ventas
                    </td>
                  </tr> : paginatedSales.map(sale => {
              const patient = patients.find(p => p.id === sale.patientId);
              return <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-4308">
                    
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-white" data-id="element-4309">
                          {sale.date}
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white" data-id="element-4310">
                          {patient?.name || 'Desconocido'}
                        </td>
                        <td className="py-3 px-4" data-id="element-4311">
                          <div className="flex flex-col gap-1" data-id="element-4312">
                            {sale.items.map((item, idx) => {
                      const product = products.find(p => p.id === item.productId);
                      const hasDiscount = (item as any).discount > 0;
                      return <span key={idx} className="text-xs text-gray-600 dark:text-gray-300" data-id="element-4313">
                              
                                  {item.quantity}x{' '}
                                  {product?.name || item.productName || 'Producto eliminado'}
                                  {hasDiscount && <span className="ml-1 text-emerald-600 dark:text-emerald-400 font-medium" data-id="element-4314">
                                      (-{(item as any).discount}%)
                                    </span>}
                                </span>;
                    })}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-bold text-orange-600 dark:text-orange-400" data-id="element-4315">
                          {formatCurrency(sale.total)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500 flex items-center gap-1.5 mt-1" data-id="element-4316">
                          <UserIcon className="w-3.5 h-3.5" data-id="element-4317" />
                          {sale.createdByUserName}
                        </td>
                        {canEditSales && <td className="py-3 px-4 text-right" data-id="element-4318">
                            <button onClick={() => handleOpenEditSaleModal(sale)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar venta" data-id="element-4319">
                        
                              <EditIcon className="w-4 h-4" data-id="element-4320" />
                            </button>
                          </td>}
                      </tr>;
            })}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-4321">
            <Pagination currentPage={salePage} totalPages={saleTotalPages} totalItems={saleTotalItems} startIndex={saleStartIndex} endIndex={saleEndIndex} onPageChange={saleGoToPage} onNext={saleNextPage} onPrev={salePrevPage} data-id="element-4322" />
          
          </div>
        </Card>}

      {/* ── STATS TAB ─────────────────────────────────────────────────────── */}
      {activeTab === 'stats' && canViewProductStats && <div className="space-y-6" data-id="element-4323">

          {/* Read-only notice */}
          {statsPermLevel === 'read' && <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300" data-id="element-4324">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-4325">
            
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" data-id="element-4326" />
            
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" data-id="element-4327" />
            
              </svg>
              <span data-id="element-4328">
                Estás en modo <strong data-id="element-4329">solo lectura</strong>. Puedes consultar
                las estadísticas pero no modificar datos desde esta vista.
              </span>
            </div>}

          {/* Filters row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-4330">
            {/* Date filter */}
            <Card className="lg:col-span-1" data-id="element-4331">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-1.5" data-id="element-4332">
                <BarChart2Icon className="w-3.5 h-3.5 text-amber-500" data-id="element-4333" /> Período
              </p>
              <div className="flex flex-col gap-2" data-id="element-4334">
                <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5" data-id="element-4335">
                  <span className="text-xs text-gray-400 w-8 flex-shrink-0" data-id="element-4336">
                    Desde
                  </span>
                  <input type="date" value={statsDateFrom} onChange={e => setStatsDateFrom(e.target.value)} className="bg-transparent text-sm border-none focus:ring-0 text-gray-700 dark:text-gray-300 flex-1 min-w-0" data-id="element-4337" />
                
                </div>
                <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5" data-id="element-4338">
                  <span className="text-xs text-gray-400 w-8 flex-shrink-0" data-id="element-4339">
                    Hasta
                  </span>
                  <input type="date" value={statsDateTo} onChange={e => setStatsDateTo(e.target.value)} className="bg-transparent text-sm border-none focus:ring-0 text-gray-700 dark:text-gray-300 flex-1 min-w-0" data-id="element-4340" />
                
                </div>
              </div>
            </Card>

            {/* Product multi-selector */}
            <Card className="lg:col-span-2 overflow-hidden" padding="none" data-id="element-4341">
              <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between" data-id="element-4342">
                <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1.5" data-id="element-4343">
                  <PackageIcon className="w-3.5 h-3.5 text-amber-500" data-id="element-4344" />
                  Filtrar productos
                  {selectedStatProducts.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" data-id="element-4345">
                      {selectedStatProducts.length} seleccionado
                      {selectedStatProducts.length > 1 ? 's' : ''}
                    </span>}
                </p>
                <div className="flex items-center gap-2" data-id="element-4346">
                  <button onClick={() => setSelectedStatProducts(products.filter(p => p.status as any !== 'archived').map(p => p.id))} className="text-xs text-amber-600 dark:text-amber-400 hover:underline" data-id="element-4347">
                  
                    Todos
                  </button>
                  <span className="text-gray-300 text-xs" data-id="element-4348">·</span>
                  <button onClick={() => setSelectedStatProducts([])} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:underline" data-id="element-4349">
                  
                    Limpiar
                  </button>
                </div>
              </div>
              {/* Search */}
              <div className="px-3 pt-3 pb-2" data-id="element-4350">
                <div className="relative" data-id="element-4351">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" data-id="element-4352" />
                  <input type="text" placeholder="Buscar producto..." value={productSelectorSearch} onChange={e => setProductSelectorSearch(e.target.value)} className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400" data-id="element-4353" />
                
                </div>
              </div>
              {/* Product chips */}
              <div className="px-3 pb-3 max-h-36 overflow-y-auto flex flex-wrap gap-2" data-id="element-4354">
                {products.filter(p => p.status as any !== 'archived').filter(p => p.name.toLowerCase().includes(productSelectorSearch.toLowerCase())).map(p => {
              const selected = selectedStatProducts.includes(p.id);
              return <button key={p.id} onClick={() => setSelectedStatProducts(selected ? selectedStatProducts.filter(id => id !== p.id) : [...selectedStatProducts, p.id])} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${selected ? 'bg-amber-500 border-amber-500 text-white shadow-sm shadow-amber-300/30' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-amber-400 hover:text-amber-600'}`} data-id="element-4355">
                    
                        {selected && <span className="text-[10px]" data-id="element-4356">✓</span>}
                        {p.name}
                      </button>;
            })}
                {products.filter(p => p.status as any !== 'archived').filter(p => p.name.toLowerCase().includes(productSelectorSearch.toLowerCase())).length === 0 && <p className="text-xs text-gray-400 italic py-2" data-id="element-4357">
                    Sin resultados
                  </p>}
              </div>
              <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800" data-id="element-4358">
                <p className="text-[11px] text-gray-400" data-id="element-4359">
                  {selectedStatProducts.length === 0 ? 'Mostrando todos los productos. Selecciona uno o varios para filtrar.' : `Mostrando estadísticas de ${selectedStatProducts.length} producto${selectedStatProducts.length > 1 ? 's' : ''}.`}
                </p>
              </div>
            </Card>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-4360">
            {[{
          label: 'Ingresos totales',
          value: formatCurrency(totalStatsRevenue),
          icon: <DollarSignIcon className="w-5 h-5" data-id="element-4361" />,
          color: 'from-amber-500 to-orange-500',
          sub: `${productStats.length} producto${productStats.length !== 1 ? 's' : ''} vendido${productStats.length !== 1 ? 's' : ''}`
        }, {
          label: 'Unidades vendidas',
          value: totalStatsUnits.toLocaleString(),
          icon: <BoxIcon className="w-5 h-5" data-id="element-4362" />,
          color: 'from-blue-500 to-indigo-500',
          sub: `${sales.filter(s => (!statsDateFrom || s.date >= statsDateFrom) && (!statsDateTo || s.date <= statsDateTo)).length} ventas`
        }, {
          label: 'Ganancia estimada',
          value: formatCurrency(totalStatsProfit),
          icon: totalStatsProfit >= 0 ? <TrendingUpIcon className="w-5 h-5" data-id="element-4363" /> : <TrendingDownIcon className="w-5 h-5" data-id="element-4364" />,
          color: totalStatsProfit >= 0 ? 'from-emerald-500 to-green-500' : 'from-red-500 to-rose-500',
          sub: 'Ingresos − Costo'
        }].map(kpi => <div key={kpi.label} className="relative overflow-hidden rounded-2xl p-5 text-white shadow-lg" data-id="element-4365">
            
                <div className={`absolute inset-0 bg-gradient-to-br ${kpi.color}`} data-id="element-4366" />
            
                <div className="relative z-10" data-id="element-4367">
                  <div className="flex items-center justify-between mb-3" data-id="element-4368">
                    <p className="text-sm font-medium text-white/80" data-id="element-4369">
                      {kpi.label}
                    </p>
                    <div className="p-2 bg-white/20 rounded-xl" data-id="element-4370">{kpi.icon}</div>
                  </div>
                  <p className="text-2xl font-bold tracking-tight" data-id="element-4371">
                    {kpi.value}
                  </p>
                  <p className="text-xs text-white/70 mt-1" data-id="element-4372">{kpi.sub}</p>
                </div>
              </div>)}
          </div>

          {/* Revenue trend */}
          <Card padding="none" className="overflow-hidden" data-id="element-4373">
            <div className="p-4 border-b border-gray-100 dark:border-gray-800" data-id="element-4374">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-4375">
                <TrendingUpIcon className="w-4 h-4 text-amber-500" data-id="element-4376" />
                Tendencia de ingresos mensual
              </h3>
            </div>
            <div className="p-4" data-id="element-4377">
              {revenueTrend.length < 2 ? <div className="py-10 text-center text-sm text-gray-400 italic" data-id="element-4378">
                  Amplía el rango de fechas para ver la tendencia.
                </div> : <ResponsiveContainer width="100%" height={220} data-id="element-4379">
                  <LineChart data={revenueTrend} margin={{
              left: 0,
              right: 8,
              top: 4
            }} data-id="element-4380">
                
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" data-id="element-4381" />
                    <XAxis dataKey="month" fontSize={12} stroke="#9ca3af" tickLine={false} axisLine={false} data-id="element-4382" />
                
                    <YAxis fontSize={11} stroke="#9ca3af" tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-4383" />
                
                    <Tooltip contentStyle={{
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.10)',
                padding: '10px 14px'
              }} formatter={(v: number) => [formatCurrency(v), 'Ingresos']} data-id="element-4384" />
                
                    <Line type="monotone" dataKey="amount" stroke="#f59e0b" strokeWidth={2.5} dot={{
                fill: '#f59e0b',
                r: 4
              }} activeDot={{
                r: 6
              }} data-id="element-4385" />
                
                  </LineChart>
                </ResponsiveContainer>}
            </div>
          </Card>

          {/* Bar chart – top 5 products */}
          {productStats.length > 0 && <Card padding="none" className="overflow-hidden" data-id="element-4386">
              <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between" data-id="element-4387">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-4388">
                  <BarChart2Icon className="w-4 h-4 text-amber-500" data-id="element-4389" />
                  Ingresos por producto
                </h3>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400" data-id="element-4390">
                  Top 5 más vendidos
                </span>
              </div>
              <div className="p-4" data-id="element-4391">
                <ResponsiveContainer width="100%" height={Math.max(220, top5ProductStats.length * 52)} data-id="element-4392">
              
                  <BarChart data={top5ProductStats} layout="vertical" margin={{
              left: 0,
              right: 50
            }} data-id="element-4393">
                
                    <defs data-id="element-4394">
                      <linearGradient id="prodBarGrad" x1="0" y1="0" x2="1" y2="0" data-id="element-4395">
                    
                        <stop offset="0%" stopColor="#f59e0b" data-id="element-4396" />
                        <stop offset="100%" stopColor="#ea580c" data-id="element-4397" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal vertical={false} data-id="element-4398" />
                
                    <XAxis type="number" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-4399" />
                
                    <YAxis dataKey="name" type="category" width={140} fontSize={12} tickLine={false} axisLine={false} tick={{
                fill: '#374151',
                fontWeight: 500
              }} data-id="element-4400" />
                
                    <Tooltip contentStyle={{
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.10)',
                padding: '10px 14px'
              }} formatter={(v: number) => [formatCurrency(v), 'Ingresos']} data-id="element-4401" />
                
                    <Bar dataKey="revenue" fill="url(#prodBarGrad)" radius={[0, 6, 6, 0]} label={{
                position: 'right',
                fontSize: 11,
                fill: '#6b7280',
                formatter: (v: number) => formatCurrency(v)
              }} data-id="element-4402" />
                
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>}

          {/* Detail table */}
          <Card padding="none" className="overflow-hidden" data-id="element-4403">
            <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between" data-id="element-4404">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-4405">
                <PackageIcon className="w-4 h-4 text-amber-500" data-id="element-4406" />
                Detalle por producto
              </h3>
              <span className="text-xs text-gray-400" data-id="element-4407">
                {productStats.length} productos
              </span>
            </div>
            {productStats.length === 0 ? <div className="py-14 text-center text-gray-400 text-sm italic" data-id="element-4408">
                No hay ventas para los filtros seleccionados.
              </div> : <>
                {/* ── MOBILE CARDS ── */}
                <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4409">
                  {paginatedProductStats.map((p, i) => {
              const profit = p.revenue - p.cost;
              const pct = totalStatsRevenue > 0 ? p.revenue / totalStatsRevenue * 100 : 0;
              return <div key={p.id} className="px-4 py-3 space-y-2" data-id="element-4410">
                        <div className="flex items-start justify-between gap-2" data-id="element-4411">
                          <div className="flex-1 min-w-0" data-id="element-4412">
                            <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-4413">
                              {p.name}
                            </p>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 mt-1" data-id="element-4414">
                              <TagIcon className="w-3 h-3" data-id="element-4415" />{p.category}
                            </span>
                          </div>
                          <p className="text-sm font-bold text-amber-600 dark:text-amber-400 whitespace-nowrap" data-id="element-4416">
                            {formatCurrency(p.revenue)}
                          </p>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs" data-id="element-4417">
                          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-2 py-1.5 text-center" data-id="element-4418">
                            <p className="text-gray-400" data-id="element-4419">Unidades</p>
                            <p className="font-bold text-gray-700 dark:text-gray-200" data-id="element-4420">{p.units}</p>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-2 py-1.5 text-center" data-id="element-4421">
                            <p className="text-gray-400" data-id="element-4422">Ventas</p>
                            <p className="font-bold text-gray-700 dark:text-gray-200" data-id="element-4423">{p.salesCount}</p>
                          </div>
                          <div className={`rounded-lg px-2 py-1.5 text-center ${profit >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-red-50 dark:bg-red-900/20'}`} data-id="element-4424">
                            <p className="text-gray-400" data-id="element-4425">Ganancia</p>
                            <p className={`font-bold text-xs ${profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} data-id="element-4426">
                              {profit >= 0 ? '+' : ''}{formatCurrency(profit)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-4427">
                          <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-1.5 overflow-hidden" data-id="element-4428">
                            <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full" style={{
                      width: `${pct}%`
                    }} data-id="element-4429" />
                          </div>
                          <span className="text-xs text-gray-400 w-10 text-right" data-id="element-4430">{pct.toFixed(1)}%</span>
                        </div>
                      </div>;
            })}
                  {/* Mobile total row */}
                  <div className="px-4 py-3 bg-amber-50/50 dark:bg-amber-900/10 border-t-2 border-gray-200 dark:border-gray-700" data-id="element-4431">
                    <div className="flex items-center justify-between mb-2" data-id="element-4432">
                      <span className="text-sm font-bold text-gray-700 dark:text-gray-300" data-id="element-4433">TOTAL GENERAL</span>
                      <span className="text-sm font-bold text-amber-600 dark:text-amber-400" data-id="element-4434">{formatCurrency(totalStatsRevenue)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs" data-id="element-4435">
                      <div className="bg-white dark:bg-gray-800 rounded-lg px-2 py-1.5 text-center" data-id="element-4436">
                        <p className="text-gray-400" data-id="element-4437">Unidades</p>
                        <p className="font-bold text-gray-700 dark:text-gray-200" data-id="element-4438">{totalStatsUnits}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg px-2 py-1.5 text-center" data-id="element-4439">
                        <p className="text-gray-400" data-id="element-4440">Ventas</p>
                        <p className="font-bold text-gray-700 dark:text-gray-200" data-id="element-4441">{productStats.reduce((s, p) => s + p.salesCount, 0)}</p>
                      </div>
                      <div className={`rounded-lg px-2 py-1.5 text-center ${totalStatsProfit >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-red-50 dark:bg-red-900/20'}`} data-id="element-4442">
                        <p className="text-gray-400" data-id="element-4443">Ganancia</p>
                        <p className={`font-bold ${totalStatsProfit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} data-id="element-4444">
                          {totalStatsProfit >= 0 ? '+' : ''}{formatCurrency(totalStatsProfit)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── DESKTOP TABLE ── */}
                <div className="hidden sm:block overflow-x-auto" data-id="element-4445">
                <table className="w-full" data-id="element-4446">
                  <thead data-id="element-4447">
                    <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50" data-id="element-4448">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4449">
                        #
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4450">
                        Producto
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell" data-id="element-4451">
                        Categoría
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4452">
                        Unidades
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4453">
                        Ventas
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4454">
                        Costo
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4455">
                        Ingresos
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4456">
                        Ganancia
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell" data-id="element-4457">
                        % del total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4458">
                    {paginatedProductStats.map((p, i) => {
                  const profit = p.revenue - p.cost;
                  const pct = totalStatsRevenue > 0 ? p.revenue / totalStatsRevenue * 100 : 0;
                  const globalIndex = statsTableStartIndex + i;
                  return <tr key={p.id} className="hover:bg-amber-50/40 dark:hover:bg-amber-900/10 transition-colors" data-id="element-4459">
                      
                          <td className="py-3 px-4 text-sm text-gray-400 font-medium" data-id="element-4460">
                            {globalIndex + 1}
                          </td>
                          <td className="py-3 px-4" data-id="element-4461">
                            <p className="font-semibold text-gray-900 dark:text-white text-sm" data-id="element-4462">
                              {p.name}
                            </p>
                          </td>
                          <td className="py-3 px-4 hidden sm:table-cell" data-id="element-4463">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400" data-id="element-4464">
                              <TagIcon className="w-3 h-3" data-id="element-4465" />
                              {p.category}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4466">
                            {p.units.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-gray-500" data-id="element-4467">
                            {p.salesCount}
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-gray-500" data-id="element-4468">
                            {formatCurrency(p.cost)}
                          </td>
                          <td className="py-3 px-4 text-right text-sm font-bold text-amber-600 dark:text-amber-400" data-id="element-4469">
                            {formatCurrency(p.revenue)}
                          </td>
                          <td className="py-3 px-4 text-right" data-id="element-4470">
                            <span className={`text-sm font-bold ${profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} data-id="element-4471">
                          
                              {profit >= 0 ? '+' : ''}
                              {formatCurrency(profit)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right hidden md:table-cell" data-id="element-4472">
                            <div className="flex items-center justify-end gap-2" data-id="element-4473">
                              <div className="w-16 bg-gray-100 dark:bg-gray-800 rounded-full h-1.5 overflow-hidden" data-id="element-4474">
                                <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full" style={{
                            width: `${pct}%`
                          }} data-id="element-4475" />
                            
                              </div>
                              <span className="text-xs text-gray-500 w-9 text-right" data-id="element-4476">
                                {pct.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>;
                })}
                  </tbody>
                  <tfoot data-id="element-4477">
                    <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-amber-50/50 dark:bg-amber-900/10" data-id="element-4478">
                      <td colSpan={3} className="py-3 px-4 text-sm font-bold text-gray-700 dark:text-gray-300" data-id="element-4479">
                    
                        TOTAL
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-gray-700 dark:text-gray-300" data-id="element-4480">
                        {totalStatsUnits.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-gray-500" data-id="element-4481">
                        {productStats.reduce((s, p) => s + p.salesCount, 0)}
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-gray-500" data-id="element-4482">
                        {formatCurrency(productStats.reduce((s, p) => s + p.cost, 0))}
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-amber-600 dark:text-amber-400" data-id="element-4483">
                        {formatCurrency(totalStatsRevenue)}
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-4484">
                        <span className={`text-sm font-bold ${totalStatsProfit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} data-id="element-4485">
                      
                          {totalStatsProfit >= 0 ? '+' : ''}
                          {formatCurrency(totalStatsProfit)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-xs text-gray-400 hidden md:table-cell" data-id="element-4486">
                        100%
                      </td>
                    </tr>
                  </tfoot>
                </table>
                {/* Pagination for detail table */}
                {statsTableTotalPages > 1 && <div className="p-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-4" data-id="element-4487">
                    <span className="text-xs text-gray-500" data-id="element-4488">
                      Mostrando {statsTableStartIndex + 1}–{statsTableEndIndex}{' '}
                      de {statsTableTotalItems} productos
                    </span>
                    <Pagination currentPage={statsTablePage} totalPages={statsTableTotalPages} onPageChange={statsTableGoToPage} data-id="element-4489" />
              
                  </div>}
              </div>
          </>}
          </Card>
        </div>}

      {/* ── ARCHIVED TAB ──────────────────────────────────────────────────── */}
      {activeTab === 'archived' && <Card padding="none" data-id="element-4490">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3" data-id="element-4491">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg" data-id="element-4492">
              <ArchiveIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" data-id="element-4493" />
            </div>
            <div data-id="element-4494">
              <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-4495">
                Productos Archivados
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4496">
                {archivedProducts.length} producto
                {archivedProducts.length !== 1 ? 's' : ''} archivado
                {archivedProducts.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {archivedProducts.length === 0 ? <div className="py-16 text-center" data-id="element-4497">
              <ArchiveIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-4498" />
              <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-4499">
                No hay productos archivados
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-4500">
                Los productos que archive aparecerán aquí
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-4501">
              <table className="w-full" data-id="element-4502">
                <thead data-id="element-4503">
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/10" data-id="element-4504">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4505">
                      Producto
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell" data-id="element-4506">
                      SKU
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell" data-id="element-4507">
                      Categoría
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4508">
                      Precio
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4509">
                      Restaurar
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4510">
                  {archivedProducts.map(product => <tr key={product.id} className="hover:bg-amber-50/50 dark:hover:bg-amber-900/10 opacity-75" data-id="element-4511">
                
                      <td className="py-3 px-4" data-id="element-4512">
                        <p className="font-medium text-gray-700 dark:text-gray-300" data-id="element-4513">
                          {product.name}
                        </p>
                        <p className="text-xs text-gray-400 truncate max-w-[200px]" data-id="element-4514">
                          {product.description}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400 hidden sm:table-cell" data-id="element-4515">
                        {product.sku}
                      </td>
                      <td className="py-3 px-4 hidden sm:table-cell" data-id="element-4516">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400" data-id="element-4517">
                          <TagIcon className="w-3 h-3" data-id="element-4518" />
                          {product.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400" data-id="element-4519">
                        {formatCurrency(product.price)}
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-4520">
                        <button onClick={() => handleUnarchiveProduct(product.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 rounded-lg transition-colors" title="Restaurar al catálogo" data-id="element-4521">
                    
                          <ArchiveRestoreIcon className="w-3.5 h-3.5" data-id="element-4522" />
                          Restaurar
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </Card>}

      {/* Product Modal */}
      <Modal isOpen={isProductModalOpen} onClose={() => setIsProductModalOpen(false)} title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'} size="md" icon={<PackageIcon className="w-6 h-6 text-white" data-id="element-4524" />} accentColor="amber" data-id="element-4523">
        
        <form onSubmit={handleSaveProduct} className="space-y-6" data-id="element-4525">
          <FormSection title="Información Básica" data-id="element-4526">
            <div className="space-y-4" data-id="element-4527">
              <Input label="Nombre del Producto" value={productForm.name} onChange={e => setProductForm({
              ...productForm,
              name: e.target.value
            })} error={productErrors.name} data-id="element-4528" />
              

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4529">
                <Input label="SKU (Código)" value={productForm.sku} onChange={e => setProductForm({
                ...productForm,
                sku: e.target.value
              })} error={productErrors.sku} data-id="element-4530" />
                

                <Select label="Categoría" options={CATEGORIES} value={productForm.category} onChange={v => setProductForm({
                ...productForm,
                category: v
              })} data-id="element-4531" />
                
              </div>
              <Textarea label="Descripción" value={productForm.description} onChange={e => setProductForm({
              ...productForm,
              description: e.target.value
            })} rows={2} data-id="element-4532" />
              
            </div>
          </FormSection>

          <FormSection title="Inventario y Precios" data-id="element-4533">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-4534">
              <Input type="number" label="Costo" value={productForm.cost} onChange={e => setProductForm({
              ...productForm,
              cost: e.target.value
            })} error={productErrors.cost} data-id="element-4535" />
              

              <Input type="number" label="Precio Venta" value={productForm.price} onChange={e => setProductForm({
              ...productForm,
              price: e.target.value
            })} error={productErrors.price} data-id="element-4536" />
              

              <Input type="number" label="Stock" value={productForm.stock} onChange={e => setProductForm({
              ...productForm,
              stock: e.target.value
            })} error={productErrors.stock} data-id="element-4537" />

              <Input type="number" label="Stock Mínimo (alerta)" placeholder="5" value={productForm.minStock} onChange={e => setProductForm({
              ...productForm,
              minStock: e.target.value
            })} hint="Se enviará una alerta cuando el stock llegue a este número o menos." data-id="element-4538" />
              
            </div>
            <div className="mt-4" data-id="element-4539">
              <Select label="Estado" options={[{
              value: 'active',
              label: 'Activo'
            }, {
              value: 'inactive',
              label: 'Inactivo'
            }]} value={productForm.status} onChange={v => setProductForm({
              ...productForm,
              status: v as any
            })} data-id="element-4540" />
              
            </div>
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800 modal-actions" data-id="element-4541">
            <Button variant="outline" type="button" onClick={() => setIsProductModalOpen(false)} data-id="element-4542">
              
              Cancelar
            </Button>
            <Button type="submit" data-id="element-4543">
              {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Sale Modal */}
      <Modal isOpen={isSaleModalOpen} onClose={() => {
      setIsSaleModalOpen(false);
      setEditingSale(null);
    }} title={editingSale ? 'Editar Venta' : 'Registrar Venta'} size="lg" icon={<ShoppingCartIcon className="w-6 h-6 text-white" data-id="element-4545" />} accentColor="amber" data-id="element-4544">
        
        {editingSale && <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-300" data-id="element-4546">
            <AlertTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-4547" />
            <span data-id="element-4548">
              Estás editando una venta existente. El stock se ajustará
              automáticamente. Este cambio quedará registrado en la bitácora.
            </span>
          </div>}
        <form onSubmit={handleSaveSale} className="space-y-6" data-id="element-4549">
          <FormSection title="Detalles de la Venta" data-id="element-4550">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4551">
              <Select label="Cliente / Paciente" options={patients.map(p => ({
              value: p.id,
              label: p.name
            }))} value={saleForm.patientId} onChange={v => setSaleForm({
              ...saleForm,
              patientId: v
            })} error={saleErrors.patientId} searchable data-id="element-4552" />
              

              <Select label="Vendedor" options={sellers.map(s => ({
              value: s.id,
              label: s.name
            }))} value={saleForm.sellerId} onChange={v => setSaleForm({
              ...saleForm,
              sellerId: v
            })} error={saleErrors.sellerId} searchable placeholder="Seleccionar vendedor..." data-id="element-4553" />
              

              <Input type="date" label="Fecha" value={saleForm.date} onChange={e => setSaleForm({
              ...saleForm,
              date: e.target.value
            })} error={saleErrors.date} data-id="element-4554" />
              
            </div>
          </FormSection>

          <FormSection title="Productos" data-id="element-4555">
            {saleErrors.items && <p className="text-sm text-red-500 mb-2" data-id="element-4556">{saleErrors.items}</p>}
            <div className="space-y-3" data-id="element-4557">
              {saleItems.map((item, idx) => <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-4558">
                
                  <div className="flex-1" data-id="element-4559">
                    <Select options={products.filter(p => p.status === 'active').map(p => ({
                  value: p.id,
                  label: `${p.name} (${p.stock} disp.) - ${formatCurrency(p.price)}`
                }))} value={item.productId} onChange={v => updateSaleItem(idx, 'productId', v)} searchable placeholder="Seleccionar producto..." data-id="element-4560" />
                  

                    {saleErrors[`item_${idx}`] && <p className="text-xs text-red-500 mt-1" data-id="element-4561">
                        {saleErrors[`item_${idx}`]}
                      </p>}
                  </div>
                  <div className="w-20" data-id="element-4562">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block" data-id="element-4563">
                      Cant.
                    </label>
                    <Input type="number" min="1" value={item.quantity.toString()} onChange={e => updateSaleItem(idx, 'quantity', parseInt(e.target.value) || 1)} data-id="element-4564" />
                  
                  </div>
                  <div className="w-20" data-id="element-4565">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block" data-id="element-4566">
                      Desc. %
                    </label>
                    <Input type="number" min="0" max="100" value={item.discount.toString()} onChange={e => updateSaleItem(idx, 'discount', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))} data-id="element-4567" />
                  
                  </div>
                  {item.productId && item.discount > 0 && <div className="flex flex-col justify-end pb-1" data-id="element-4568">
                      <span className="text-xs text-gray-400 line-through" data-id="element-4569">
                        {formatCurrency((products.find(p => p.id === item.productId)?.price || 0) * item.quantity)}
                      </span>
                      <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-id="element-4570">
                        {formatCurrency((products.find(p => p.id === item.productId)?.price || 0) * item.quantity * (1 - item.discount / 100))}
                      </span>
                    </div>}
                  <button type="button" onClick={() => removeSaleItem(idx)} className="p-2 mt-1 text-red-500 hover:bg-red-50 rounded-lg" disabled={saleItems.length === 1} data-id="element-4571">
                  
                    <TrashIcon className="w-5 h-5" data-id="element-4572" />
                  </button>
                </div>)}
              <Button type="button" variant="outline" size="sm" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-4574" />} onClick={addSaleItem} data-id="element-4573">
                
                Agregar otro producto
              </Button>
            </div>
            <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl flex items-center justify-between border border-orange-100 dark:border-orange-800" data-id="element-4575">
              <span className="text-orange-800 dark:text-orange-300 font-medium" data-id="element-4576">
                Total de la Venta:
              </span>
              <span className="text-2xl font-bold text-orange-600 dark:text-orange-400" data-id="element-4577">
                {formatCurrency(calculateSaleTotal())}
              </span>
            </div>
          </FormSection>

          <FormSection title="Notas Adicionales" data-id="element-4578">
            <Textarea value={saleForm.notes} onChange={e => setSaleForm({
            ...saleForm,
            notes: e.target.value
          })} rows={2} placeholder="Observaciones sobre la venta..." data-id="element-4579" />
            
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800 modal-actions" data-id="element-4580">
            <Button variant="outline" type="button" onClick={() => {
            setIsSaleModalOpen(false);
            setEditingSale(null);
          }} data-id="element-4581">
              
              Cancelar
            </Button>
            <Button type="submit" leftIcon={<DollarSignIcon className="w-4 h-4" data-id="element-4583" />} data-id="element-4582">
              
              {editingSale ? 'Guardar Cambios' : 'Registrar Venta'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Archive Confirm Modal ─────────────────────────────────────── */}
      {archiveConfirmProduct && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-4584">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !archiveLoading && setArchiveConfirmProduct(null)} data-id="element-4585" />
        

          {/* Card */}
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-4586">
            {/* Top accent bar */}
            <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" data-id="element-4587" />
            <div className="p-6" data-id="element-4588">
              {/* Icon + title */}
              <div className="flex items-start gap-4 mb-4" data-id="element-4589">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center" data-id="element-4590">
                  <ArchiveIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" data-id="element-4591" />
                </div>
                <div data-id="element-4592">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-4593">
                    ¿Archivar producto?
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-4594">
                    Esta acción no eliminará el producto permanentemente.
                  </p>
                </div>
              </div>

              {/* Product info */}
              <div className="mb-5 p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" data-id="element-4595">
                <div className="flex items-center gap-3" data-id="element-4596">
                  <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center" data-id="element-4597">
                    <PackageIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" data-id="element-4598" />
                  </div>
                  <div data-id="element-4599">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm" data-id="element-4600">
                      {archiveConfirmProduct.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4601">
                      SKU: {archiveConfirmProduct.sku} ·{' '}
                      {archiveConfirmProduct.category}
                    </p>
                  </div>
                </div>
              </div>

              {/* Info note */}
              <div className="flex items-start gap-2 p-3 mb-6 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300" data-id="element-4602">
                <ArchiveRestoreIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-4603" />
                <span data-id="element-4604">
                  Podrás restaurarlo en cualquier momento desde la pestaña{' '}
                  <strong data-id="element-4605">Productos Archivados</strong>.
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-3" data-id="element-4606">
                <button onClick={() => setArchiveConfirmProduct(null)} disabled={archiveLoading} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50" data-id="element-4607">
                
                  Cancelar
                </button>
                <button onClick={confirmArchiveProduct} disabled={archiveLoading} className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-semibold shadow-md shadow-orange-200 dark:shadow-orange-900/30 transition-all disabled:opacity-70 flex items-center justify-center gap-2" data-id="element-4608">
                
                  {archiveLoading ? <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" data-id="element-4609">
                    
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-4610" />
                    
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" data-id="element-4611" />
                    
                      </svg>
                      Archivando...
                    </> : <>
                      <ArchiveIcon className="w-4 h-4" data-id="element-4612" />
                      Sí, archivar
                    </>}
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Productos" isExporting={isExporting} data-id="element-4613" />
      

      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Productos a PDF" isExporting={isPdfExporting} data-id="element-4614" />
      

      {/* Sales Export Modals */}
      <ExcelExportModal isOpen={isSalesExcelModalOpen} onClose={() => setIsSalesExcelModalOpen(false)} onExport={handleSalesExportToExcel} title="Exportar Ventas a Excel" isExporting={isSalesExcelExporting} data-id="element-4615" />
      

      <PdfExportModal isOpen={isSalesPdfModalOpen} onClose={() => setIsSalesPdfModalOpen(false)} onExport={handleSalesExportToPdf} title="Exportar Ventas a PDF" isExporting={isSalesPdfExporting} data-id="element-4616" />
      

      {/* Stats Export Modals */}
      <ExcelExportModal isOpen={isStatsExcelModalOpen} onClose={() => setIsStatsExcelModalOpen(false)} onExport={handleStatsExportToExcel} title="Exportar Estadísticas a Excel" isExporting={isStatsExcelExporting} data-id="element-4617" />
      

      <PdfExportModal isOpen={isStatsPdfModalOpen} onClose={() => setIsStatsPdfModalOpen(false)} onExport={handleStatsExportToPdf} title="Exportar Estadísticas a PDF" isExporting={isStatsPdfExporting} data-id="element-4618" />
      
    </div>;
}